import React from 'react';

import Tooltip from '../../../../../controls/common/tooltip';

import WidgetStatus from '../../../common/widgetStatus';
import { describeRecord } from '../../domain/present';

/**
 * MetaThread — the cell meta-thread, in the width of a property panel.
 *
 * The cell detail surface renders this thread properly (`surfaces/cell/panes.js`); this is the
 * preview beside the mission map, and it stays a dotted timeline because 16rem of rail is not where
 * anybody reads an agent's closing message. What it must not do is DISAGREE with the full view about
 * what a record is — so both read the same `describeRecord`, both lead with the same three facts in
 * the same order, and only the density differs: what is a chip over there is a caption here.
 *
 * Being the preview is load-bearing, not a description. Every record's prose is CLAMPED here (see
 * `.mc-mt-text`): unclamped, one agent's closing message ran taller than the rail and pushed the
 * whole rest of the mission's history off the bottom of it, which is the opposite of what a
 * timeline is for. The cell view is where a record is read whole.
 *
 * It used to key its dot off a table of words that were never record types at all (`ok`, `success`,
 * `progress`, `sys`), so under any vocabulary every dot came out the default grey. `tone` is the
 * axis that actually exists: bookkeeping recedes, the moments that ended something do not.
 *
 * **It is the tail, and it says so.** Ten records (`client.previewMetaThread`), oldest of the ten
 * first, because a preview that runs past the fold has stopped being one — a long-running cell put
 * its whole history in this rail and the newest record, the one saying what the cell is doing now,
 * sat at the bottom of a wall nobody scrolls.
 *
 * When there are older ones, an ellipsis stands above the first, on a row shaped like a record — its
 * own dot on the timeline, its mark in the text column — because that is what it stands for: the
 * records not drawn. A list silently missing its beginning reads as the whole of it, and this rail
 * WAS the whole of it, so something has to be there; a sentence is not what, in 16rem of column where
 * every record already fights for width. The sentence is the tooltip.
 *
 * Props:
 *   items    — thread item array (from client.previewMetaThread); may be null/[]
 *   loading  — boolean
 *   more     — older records exist above these; the cell's own screen has them
 */

/** The dot's colour, from the record's own emphasis. */
function dotClass(tone) {
  if (tone === 'good') return 'mc-mt-dot ok';
  if (tone === 'warn') return 'mc-mt-dot error';
  if (tone === 'event') return 'mc-mt-dot sys';
  return 'mc-mt-dot step';
}

/**
 * Format a timestamp for display.  Falls back gracefully to raw string if
 * the value is not parseable.
 */
function formatTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return ts;
  }
}

function MetaThread({ items, loading, more }) {
  if (loading) {
    return <WidgetStatus variant="primary" loading>Loading thread…</WidgetStatus>;
  }

  // An ordinary state, not a failure: a DRAFT cell has no meta-thread object at all — the
  // orchestrator creates it when the cell is activated (see client.addCell) — and `listMetaThread`
  // hands back an empty list rather than throwing when the object is absent. The same words the
  // cell view uses for the same thing, and the same shape as the object tree's "No runs yet."
  if (!items || items.length === 0) {
    return <WidgetStatus variant="light">No activity yet.</WidgetStatus>;
  }

  return (
    <div className="mc-mt">
      {more && (
        <Tooltip text="Earlier records are on the cell&rsquo;s own screen">
          <div className="mc-mt-ev mc-mt-earlier"
            aria-label="Earlier records are on the cell's own screen">
            <div className="mc-mt-dot" />
            <div className="mc-mt-body">&hellip;</div>
          </div>
        </Tooltip>
      )}
      {items.map((item, idx) => {
        const shape = describeRecord(item);
        const ts = item.created_at || '';

        return (
          <div key={item.item_id || item.id || idx} className="mc-mt-ev">
            <div className={dotClass(shape.tone)} />
            <div className="mc-mt-body">
              {/* What the record IS, what it did, which run it is about and what it ended with —
                  the same four, in the same order, the full view's head leads with, with that
                  view's per-type detail trailing them exactly as it does there. What is a pill over
                  there is the last caption here, and it is read for every type: dropping it would
                  put a cell's `completed` in the full view and nowhere in the rail. This named
                  `user_id` before, which at this width is a uuid and at any width is the wrong
                  question: the thread has three writers and the record already says which one
                  produced it. */}
              <div className="mc-mt-who">
                <span>
                  {shape.type}
                  {shape.event && <> &middot; {shape.event}</>}
                  {shape.run && <> &middot; {shape.run.key}</>}
                  {shape.qualifier && <> &middot; {shape.qualifier}</>}
                  {shape.label && <> &middot; {shape.label}</>}
                </span>
                {ts && <span className="mc-mt-ts">{formatTime(ts)}</span>}
              </div>
              {shape.body && shape.body.text && (
                <div className="mc-mt-text">{shape.body.text}</div>
              )}
              {/* An attachment-only record has no prose, and a blank line in the rail reads as a
                  record that did not happen. Its files are what it said. */}
              {shape.body && !shape.body.text && shape.body.content && (
                <div className="mc-mt-text">
                  {shape.body.content.filter(block => 'file' === block.type)
                    .map(block => block.file.name).join(', ')}
                </div>
              )}
              {/* An artifact's prose is its path, size and type written out, and `describeRecord`
                  drops it in favour of the three fields — which the full view renders as a chip.
                  Here the name alone is what fits. */}
              {shape.type === 'artifact' && (
                <div className="mc-mt-text">{shape.artifactPath}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default MetaThread;
